import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import type { Id, QltClassNoteData } from '../../api/types';
import { Toast } from '../../components';
import { REMINDER_OTHER } from '../../config/reminder-presets';
import { useQltClassNoteStore } from '../../stores/qlt';
import { parentNoticeOf, sentLabel } from '../../stores/qlt/after-lesson-reminder';
import { AfterLessonReminderForm } from './AfterLessonReminderForm';
import type { ReminderSavedToast } from './AfterLessonReminderForm';
import '../../styles/class-management.css';

export interface AfterLessonReminderCardProps {
  periodLabel: string;
  date?: string;
  subjectTitle?: string;
  lessonTitle?: string;
  classNoteId?: Id;
  classId: string;
  gradeId?: Id;
  subjectId?: Id;
}

// structured presets when the form has written them, else the legacy flat string
function reminderContent(data?: QltClassNoteData): string {
  if (!data) return '';
  if (Array.isArray(data.reminderPresets)) {
    const other = (data.reminderOther || '').trim();
    return data.reminderPresets.filter((item) => item !== REMINDER_OTHER).concat(other ? [other] : []).join('; ');
  }
  return (data.classReminder || '').trim();
}

// free-text BTVN first; falls back to the assigned units and is empty on older records
function homeworkContent(data?: QltClassNoteData): string {
  if (!data) return '';
  const text = (data.homework || '').trim();
  if (text) return text;
  if (!data.homeworkEnabled || !Array.isArray(data.homeworkUnits)) return '';
  return data.homeworkUnits.map((item) => item.name).join('; ');
}

export function AfterLessonReminderCard({ periodLabel, date, subjectTitle, lessonTitle, classNoteId, classId, gradeId, subjectId }: AfterLessonReminderCardProps) {
  const { t } = useTranslation(['panels', 'common']);
  const note = useQltClassNoteStore((state) => state.currentNote);
  const linkedLesson = useQltClassNoteStore((state) => state.currentLesson);
  const [formOpen, setFormOpen] = useState(false);
  const [savedToast, setSavedToast] = useState<ReminderSavedToast | null>(null);
  const linkedName = linkedLesson?.topicLesson && typeof linkedLesson.topicLesson === 'object' ? String((linkedLesson.topicLesson as { name?: string }).name || '') : '';
  const content = reminderContent(note?.data);
  const homework = homeworkContent(note?.data);
  const parentNotice = parentNoticeOf(note?.data);

  return (
    <>
      <section className="cm-card">
        <div className="cm-card__head">
          <h3 className="cm-card__title">{t('reminder.title')}</h3>
          <button type="button" className="cm-card__more" disabled={!classNoteId || !note} onClick={() => setFormOpen(true)}>{t('common:actions.add')}</button>
        </div>
        {!classNoteId ? <p className="cm-card__empty">{t('reminder.needLogbook')}</p> : (
          <dl className="cm-card__rows">
            <div className="cm-card__row"><dt>{t('reminder.content')}</dt><dd>{content || t('empty.none')}</dd></div>
            <div className="cm-card__row"><dt>{t('reminder.homeworkShort')}</dt><dd>{homework || t('empty.none')}</dd></div>
            {/* only what the board itself sent; lms-fe keeps no record of its posts */}
            {parentNotice ? <div className="cm-card__row"><dt>{t('reminder.parents')}</dt><dd>{t('reminder.sentAt', { time: sentLabel(parentNotice.sentAt) })}</dd></div> : null}
          </dl>
        )}
      </section>
      <AfterLessonReminderForm open={formOpen} periodLabel={periodLabel} date={date} subjectTitle={subjectTitle} lessonName={linkedName || note?.lesson || lessonTitle || ''} classId={classId} gradeId={gradeId} subjectId={subjectId} onClose={() => setFormOpen(false)} onSaved={setSavedToast} />
      <Toast open={savedToast !== null} kind={savedToast ? savedToast.kind : 'success'} message={savedToast ? savedToast.message : ''} onClose={() => setSavedToast(null)} />
    </>
  );
}
